import { useFlags } from '@atlaskit/flag';
import { ButtonType } from 'core/enums/button-type';
import { useDialog } from 'core/providers/DialogProvider';
import { showSuccessFlag } from 'core/utilities/flags-helper';
import { useState } from 'react';
import { Eye, EyeOff } from 'react-feather';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import { ISharedDialogButton } from 'shared/components/dialog-base/dialog-base-interface';

interface IUserPasswordResetDialog {
  fullName: string;
  temporaryPassword: string;
}

const UserPasswordResetDialog = ({ fullName, temporaryPassword }: IUserPasswordResetDialog) => {
  const [showPassword, setShowPassword] = useState(false);
  const dialog = useDialog();
  const flags = useFlags();

  const password = showPassword
    ? temporaryPassword
    : temporaryPassword
        .split('')
        .map(() => '•')
        .join('');

  const copyPassword = () => {
    navigator.clipboard.writeText(temporaryPassword);
    showSuccessFlag('Copied to clipboard', 'The temporary password has been copied to your clipboard', flags);
  };

  const customContent = () => {
    return (
      <div className='body-sm p-4'>
        <p className='mb-4'>
          A new password has been created for <span className='font-semibold'>{fullName}</span>. They'll be asked to set
          a new password when they next login.
        </p>

        <div className='flex items-center'>
          <p>{`Password: ${password}`}</p>
          <div
            className='flex items-center justify-center w-10 text-gray-400 hover:text-gray-700 cursor-pointer'
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </div>
        </div>
      </div>
    );
  };

  const customButtons: ISharedDialogButton[] = [
    {
      key: 'copyPassword',
      buttonProps: {
        onClick: () => copyPassword(),
        type: 'button',
        appearance: 'primary',
        label: 'Copy Password',
      },
      type: ButtonType.Button,
    },
    {
      key: 'done',
      buttonProps: {
        onClick: () => dialog?.closeDialog(),
        type: 'button',
        appearance: 'primary',
        label: 'Done',
      },
      type: ButtonType.Button,
    },
  ];

  return (
    <SharedDialogBase
      title='User password successfully reset'
      customContentTemplate={customContent()}
      customButtons={customButtons}
    />
  );
};

export default UserPasswordResetDialog;
